import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Artifact, DeploymentArtifact, PackageVersion, ResolvedDeploymentConfig, Server } from '@/api/configuration';
import {
    createDeploymentArtifact,
    deleteDeploymentArtifact,
    getAllResolvedDeployments,
    listAllDeploymentArtifacts,
    listServers,
    updateDeploymentArtifact
} from '@/api/configuration';
import { triggerArtifactDeploy } from '@/api/deploy';
import type { ArtifactDeployStatusData } from '@/hooks/use-artifact-deploy-status';
import { useArtifactDeployStatuses } from '@/hooks/use-artifact-deploy-status';
import { useArtifactVersions } from '@/hooks/use-artifact-download';
import {
    useArtifacts,
    useEnvironments,
    useInvalidateConfiguration,
    useServiceDefinitions,
} from '@/hooks/use-configuration';
import { ApiError } from '@/modules/api/api-client';
import { useEnvironmentStore } from '@/stores/environment-store';

// --- Shared UI helpers ---

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-6 text-center">
      <p className="text-sm text-content-tertiary">{message}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="animate-pulse space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-12 rounded-lg bg-gray-200 dark:bg-gray-700" />
      ))}
    </div>
  );
}

// --- Override Validation ---

interface OverrideFieldValues {
  downloadPath: string;
  deployPath: string;
  port: string;
  ownerGroup: string;
  ownerName: string;
  serviceDefinitionId: string;
}

interface OverrideValidationErrors {
  downloadPath?: string;
  deployPath?: string;
  port?: string;
  ownerGroup?: string;
  ownerName?: string;
  serviceDefinitionId?: string;
}

function validateOverrideFields(values: OverrideFieldValues): OverrideValidationErrors {
  const errors: OverrideValidationErrors = {};
  const textFields: (keyof OverrideFieldValues)[] = [
    'downloadPath',
    'deployPath',
    'ownerGroup',
    'ownerName',
    'serviceDefinitionId',
  ];

  for (const field of textFields) {
    if (values[field].length > 255) {
      errors[field] = 'Must not exceed 255 characters';
    }
  }

  if (values.port.trim() !== '') {
    const portNum = Number(values.port);
    if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535) {
      errors.port = 'Port must be a number between 1 and 65535';
    }
  }

  return errors;
}

// --- Inline Override Edit Form ---

interface OverrideEditFormProps {
  artifact: Artifact;
  environmentId: string;
  projectKey: string;
  existingOverride?: DeploymentArtifact;
  resolvedConfig?: ResolvedDeploymentConfig | null;
  serviceDefinitions: { id: string; name: string }[];
  onSaved: () => Promise<void>;
  onDeleted: () => Promise<void>;
  onCancel: () => void;
}

function OverrideEditForm({
  artifact,
  environmentId,
  projectKey,
  existingOverride,
  resolvedConfig,
  serviceDefinitions,
  onSaved,
  onDeleted,
  onCancel,
}: OverrideEditFormProps) {
  const [formValues, setFormValues] = useState<OverrideFieldValues>(() => ({
    downloadPath: existingOverride?.downloadPath ?? '',
    deployPath: existingOverride?.deployPath ?? '',
    port: existingOverride?.port != null ? String(existingOverride.port) : '',
    ownerGroup: existingOverride?.ownerGroup ?? '',
    ownerName: existingOverride?.ownerName ?? '',
    serviceDefinitionId: existingOverride?.serviceDefinitionId ?? '',
  }));

  const [errors, setErrors] = useState<OverrideValidationErrors>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Check if all fields are empty/null (meaning "Using defaults")
  const allFieldsEmpty =
    formValues.downloadPath.trim() === '' &&
    formValues.deployPath.trim() === '' &&
    formValues.port.trim() === '' &&
    formValues.ownerGroup.trim() === '' &&
    formValues.ownerName.trim() === '' &&
    formValues.serviceDefinitionId.trim() === '';

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateOverrideFields(formValues);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSaveError(null);
    setSaving(true);

    const payload: Partial<DeploymentArtifact> = {
      environmentId,
      downloadPath: formValues.downloadPath || undefined,
      deployPath: formValues.deployPath || undefined,
      port: formValues.port.trim() !== '' ? Number(formValues.port) : undefined,
      ownerGroup: formValues.ownerGroup || undefined,
      ownerName: formValues.ownerName || undefined,
      serviceDefinitionId: formValues.serviceDefinitionId || undefined,
    };

    try {
      if (existingOverride) {
        await updateDeploymentArtifact(projectKey, artifact.id, existingOverride.id, payload);
      } else {
        await createDeploymentArtifact(projectKey, artifact.id, {
          environmentId,
          downloadPath: formValues.downloadPath || undefined,
          deployPath: formValues.deployPath || undefined,
          port: formValues.port.trim() !== '' ? Number(formValues.port) : undefined,
          ownerGroup: formValues.ownerGroup || undefined,
          ownerName: formValues.ownerName || undefined,
          serviceDefinitionId: formValues.serviceDefinitionId || undefined,
        });
      }
      await onSaved();
    } catch (err) {
      setSaveError(
        err instanceof ApiError
          ? err.message || 'Failed to save override'
          : 'Failed to save override. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingOverride) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteDeploymentArtifact(projectKey, artifact.id, existingOverride.id);
      await onDeleted();
    } catch (err) {
      setDeleteError(
        err instanceof ApiError
          ? err.message || 'Failed to delete override record'
          : 'Failed to delete override record. Please try again.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const updateField = (field: keyof OverrideFieldValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Get inherited default value for a field
  const getInheritedValue = (field: keyof OverrideFieldValues): string | null => {
    if (!resolvedConfig) return null;
    const fieldMap: Record<keyof OverrideFieldValues, keyof ResolvedDeploymentConfig> = {
      downloadPath: 'downloadPath',
      deployPath: 'deployPath',
      port: 'port',
      ownerGroup: 'ownerGroup',
      ownerName: 'ownerName',
      serviceDefinitionId: 'serviceDefinitionId',
    };
    const resolved = resolvedConfig[fieldMap[field]];
    if (resolved && resolved.source === 'default' && resolved.value != null) {
      return String(resolved.value);
    }
    // Fall back to artifact defaults
    switch (field) {
      case 'downloadPath': return artifact.defaultDownloadPath ?? null;
      case 'deployPath': return artifact.defaultDeployPath ?? null;
      case 'port': return artifact.defaultPort != null ? String(artifact.defaultPort) : null;
      case 'ownerGroup': return artifact.defaultOwnerGroup ?? null;
      case 'ownerName': return artifact.defaultOwnerName ?? null;
      case 'serviceDefinitionId': return artifact.defaultServiceDefinitionId ?? null;
      default: return null;
    }
  };

  const renderFieldInput = (
    field: keyof OverrideFieldValues,
    label: string,
    placeholder?: string,
    type: 'text' | 'number' | 'select' = 'text'
  ) => {
    const inheritedValue = getInheritedValue(field);
    const isCleared = formValues[field].trim() === '' && inheritedValue != null;

    if (type === 'select' && field === 'serviceDefinitionId') {
      return (
        <div key={field}>
          <label htmlFor={`override-${field}`} className="block text-xs font-medium text-content-secondary mb-1">
            {label}
          </label>
          <select
            id={`override-${field}`}
            value={formValues[field]}
            onChange={(e) => updateField(field, e.target.value)}
            className={`w-full rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600 ${
              errors[field]
                ? 'border-red-300 dark:border-red-700'
                : 'border-border bg-surface text-content'
            }`}
          >
            <option value="">— Inherit default —</option>
            {serviceDefinitions.map((sd) => (
              <option key={sd.id} value={sd.id}>{sd.name}</option>
            ))}
          </select>
          {isCleared && (
            <p className="mt-0.5 text-xs text-content-tertiary opacity-60">
              <span className="inline-flex items-center gap-1">
                <span className="rounded bg-gray-100 dark:bg-gray-800 px-1 py-0.5 text-[10px] font-medium text-content-tertiary">default</span>
                {serviceDefinitions.find((sd) => sd.id === inheritedValue)?.name ?? inheritedValue}
              </span>
            </p>
          )}
          {errors[field] && <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors[field]}</p>}
        </div>
      );
    }

    return (
      <div key={field}>
        <label htmlFor={`override-${field}`} className="block text-xs font-medium text-content-secondary mb-1">
          {label}
        </label>
        <input
          id={`override-${field}`}
          type={type === 'number' ? 'number' : 'text'}
          value={formValues[field]}
          onChange={(e) => updateField(field, e.target.value)}
          placeholder={isCleared ? inheritedValue ?? placeholder : placeholder}
          className={`w-full rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600 ${
            errors[field]
              ? 'border-red-300 dark:border-red-700'
              : isCleared
                ? 'border-border bg-surface text-content-tertiary opacity-60'
                : 'border-border bg-surface text-content'
          }`}
        />
        {isCleared && (
          <p className="mt-0.5 text-xs text-content-tertiary opacity-60">
            <span className="inline-flex items-center gap-1">
              <span className="rounded bg-gray-100 dark:bg-gray-800 px-1 py-0.5 text-[10px] font-medium text-content-tertiary">default</span>
              {inheritedValue}
            </span>
          </p>
        )}
        {errors[field] && <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors[field]}</p>}
      </div>
    );
  };

  return (
    <form onSubmit={handleSave} className="rounded-lg border border-brand-200 dark:border-brand-800 bg-surface-raised p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-content-secondary">
          Override Fields for {artifact.name}
        </p>
        {allFieldsEmpty && existingOverride && (
          <span className="inline-flex items-center gap-1.5 text-xs text-content-tertiary">
            <span className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-medium">Using defaults</span>
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {renderFieldInput('downloadPath', 'Download Path', 's3://bucket/path or URL')}
        {renderFieldInput('deployPath', 'Deploy Path', '/opt/apps/my-app')}
        {renderFieldInput('port', 'Port', '8080', 'number')}
        {renderFieldInput('ownerGroup', 'Owner Group', 'appuser')}
        {renderFieldInput('ownerName', 'Owner Name', 'appuser')}
        {renderFieldInput('serviceDefinitionId', 'Service Definition', undefined, 'select')}
      </div>

      {saveError && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-2">
          <p className="text-xs text-red-700 dark:text-red-300">{saveError}</p>
        </div>
      )}

      {deleteError && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-2">
          <p className="text-xs text-red-700 dark:text-red-300">{deleteError}</p>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || deleting}
          className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
        >
          {saving ? (
            <span className="inline-flex items-center gap-1.5">
              <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Saving...
            </span>
          ) : (
            existingOverride ? 'Update Override' : 'Save Override'
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving || deleting}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
        >
          Cancel
        </button>
        {allFieldsEmpty && existingOverride && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving || deleting}
            className="ml-auto rounded-md border border-red-200 dark:border-red-800 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            {deleting ? (
              <span className="inline-flex items-center gap-1.5">
                <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Deleting...
              </span>
            ) : (
              'Delete Override'
            )}
          </button>
        )}
      </div>
    </form>
  );
}

// --- Override Display Row (read-only, with resolved values and source badges) ---

function OverrideFieldDisplay({
  label,
  value,
  source,
  serviceDefinitions,
}: {
  label: string;
  value: string | number | null;
  source?: 'default' | 'override';
  serviceDefinitions?: { id: string; name: string }[];
}) {
  if (value == null) return null;

  const displayValue = label === 'Service Definition' && serviceDefinitions
    ? serviceDefinitions.find((sd) => sd.id === String(value))?.name ?? String(value)
    : String(value);

  const isDefault = source === 'default';

  return (
    <span className={`inline-flex items-center gap-1 ${isDefault ? 'opacity-60' : ''}`}>
      <span className="text-content-tertiary">{label}:</span>
      <span className={isDefault ? 'text-content-tertiary' : 'text-content'}>{displayValue}</span>
      {isDefault && (
        <span className="rounded bg-gray-100 dark:bg-gray-800 px-1 py-0.5 text-[10px] font-medium text-content-tertiary">
          default
        </span>
      )}
    </span>
  );
}

// --- Artifact Deploy Status Badge ---

interface ArtifactDeployStatusBadgeProps {
  data?: ArtifactDeployStatusData;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

function ArtifactDeployStatusBadge({ data, isLoading, isError, onRetry }: ArtifactDeployStatusBadgeProps) {
  // Loading state (Req 9.3)
  if (isLoading) {
    return (
      <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-content-tertiary">
        <span className="inline-block h-2 w-2 rounded-full bg-gray-300 animate-pulse" />
        Loading status...
      </span>
    );
  }

  // Error with retry (Req 9.5)
  if (isError) {
    return (
      <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
        <span className="inline-block h-2 w-2 rounded-full bg-red-400" />
        Status retrieval failed
        <button
          type="button"
          onClick={onRetry}
          className="ml-1 underline text-xs text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-200 focus:outline-none"
        >
          Retry
        </button>
      </span>
    );
  }

  // Never deployed (Req 9.4)
  if (!data || (!data.lastSuccess && !data.lastFailure)) {
    return (
      <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-content-tertiary">
        <span className="inline-block h-2 w-2 rounded-full bg-gray-400" />
        Never deployed
      </span>
    );
  }

  const lastSuccess = data.lastSuccess;
  const lastFailure = data.lastFailure;
  const successTime = lastSuccess?.completed ?? lastSuccess?.started ?? '';
  const failureTime = lastFailure?.completed ?? lastFailure?.started ?? '';
  const lastWasFailure = lastFailure && (!lastSuccess || failureTime > successTime);

  // Failure badge with last success version (Req 9.6)
  if (lastWasFailure) {
    const date = lastFailure.completed ?? lastFailure.started;
    const dateStr = date ? `${new Date(date).toLocaleDateString()} at ${new Date(date).toLocaleTimeString()}` : '';
    const versionStr = lastFailure.version ? ` v${lastFailure.version}` : '';
    return (
      <div className="mt-1 flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
          <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
          Failed{versionStr} <span className="text-content-tertiary">({dateStr})</span>
        </span>
        {lastSuccess && (
          <span className="inline-flex items-center gap-1.5 text-xs text-content-tertiary pl-3.5">
            Last success: <strong className="text-content-secondary">v{lastSuccess.version}</strong>
            {lastSuccess.completed && <span>({new Date(lastSuccess.completed).toLocaleDateString()})</span>}
          </span>
        )}
      </div>
    );
  }

  // Success badge (Req 9.1)
  const date = lastSuccess!.completed ?? lastSuccess!.started;
  const dateStr = date ? `${new Date(date).toLocaleDateString()} at ${new Date(date).toLocaleTimeString()}` : '';
  const versionStr = lastSuccess!.version ? ` v${lastSuccess!.version}` : '';

  return (
    <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-content-secondary">
      <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
      Deployed{versionStr} <span className="text-content-tertiary">({dateStr})</span>
    </span>
  );
}

// --- Deploy Modal ---

function DeployModal({
  artifact,
  projectKey,
  environmentId,
  onClose,
  onDeploy,
}: {
  artifact: Artifact;
  projectKey: string;
  environmentId: string;
  onClose: () => void;
  onDeploy: (environmentId: string, version: string, server?: string) => Promise<void>;
}) {
  const [selectedServer, setSelectedServer] = useState('');
  const [servers, setServers] = useState<Server[]>([]);
  const [loadingServers, setLoadingServers] = useState(false);
  const [version, setVersion] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch latest versions from CodeArtifact
  const { data: allVersions = [], isLoading: versionsLoading } = useArtifactVersions(projectKey, artifact.id);

  // Sort by semver descending (newest first) and take only the latest 5
  const recentVersions = useMemo<PackageVersion[]>(() => {
    const sorted = [...allVersions].sort((a, b) => {
      const partsA = a.version.split(/[.\-]/).map((p) => { const n = parseInt(p, 10); return isNaN(n) ? p : n; });
      const partsB = b.version.split(/[.\-]/).map((p) => { const n = parseInt(p, 10); return isNaN(n) ? p : n; });
      const len = Math.max(partsA.length, partsB.length);
      for (let i = 0; i < len; i++) {
        const pa = partsA[i];
        const pb = partsB[i];
        if (pa === undefined && pb !== undefined) return 1;
        if (pa !== undefined && pb === undefined) return -1;
        if (typeof pa === 'number' && typeof pb === 'number') {
          if (pa !== pb) return pb - pa;
        } else {
          const sa = String(pa);
          const sb = String(pb);
          if (sa !== sb) return sb.localeCompare(sa);
        }
      }
      return 0;
    });
    return sorted.slice(0, 5);
  }, [allVersions]);

  // Filter suggestions based on current input
  const filteredSuggestions = useMemo<PackageVersion[]>(() => {
    if (!version.trim()) return recentVersions;
    return recentVersions.filter((v: PackageVersion) =>
      v.version.toLowerCase().includes(version.toLowerCase())
    );
  }, [recentVersions, version]);

  const latestVersion = recentVersions.length > 0 ? recentVersions[0]!.version : null;

  // Track page-level environment — close modal if it changes
  const { selectedEnvironmentId: storeEnvId } = useEnvironmentStore();
  const initialEnvRef = useRef(environmentId);

  useEffect(() => {
    if (storeEnvId !== initialEnvRef.current) {
      onClose();
    }
  }, [storeEnvId, onClose]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load servers filtered by the page-level environment
  useEffect(() => {
    if (!environmentId) {
      setServers([]);
      setSelectedServer('');
      return;
    }
    let cancelled = false;
    setLoadingServers(true);
    listServers(projectKey, environmentId)
      .then((result) => {
        if (cancelled) return;
        setServers(result);
        setSelectedServer(result.length === 1 ? (result[0]!.inventoryName || result[0]!.id) : '');
      })
      .catch(() => { if (!cancelled) setServers([]); })
      .finally(() => { if (!cancelled) setLoadingServers(false); });
    return () => { cancelled = true; };
  }, [environmentId, projectKey]);

  const noServersAvailable = !loadingServers && servers.length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!environmentId || !version.trim() || !selectedServer || noServersAvailable) return;
    setDeploying(true);
    setError(null);
    try {
      await onDeploy(environmentId, version.trim(), selectedServer);
      setSuccess(`Deployment queued for ${artifact.name} v${version.trim()}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger deployment');
    } finally {
      setDeploying(false);
    }
  };

  const selectVersion = (v: string) => {
    setVersion(v);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="deploy-modal-title"
        className="relative z-10 w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-xl"
      >
        <h2 id="deploy-modal-title" className="text-lg font-semibold text-content mb-1">
          Deploy {artifact.name}
        </h2>
        <p className="text-xs text-content-tertiary mb-4">
          Select a server and specify the version to deploy.
        </p>

        {success ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3">
              <p className="text-sm font-medium text-green-700 dark:text-green-300">{success}</p>
            </div>
            <button type="button" onClick={onClose} className="w-full rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Server dropdown */}
            <div>
              <label htmlFor="deploy-server" className="block text-xs font-medium text-content-secondary mb-1">Server *</label>
              {loadingServers && (
                <p className="text-xs text-content-tertiary">Loading servers...</p>
              )}
              {!loadingServers && servers.length > 0 && (
                <select
                  id="deploy-server"
                  required
                  value={selectedServer}
                  onChange={(e) => setSelectedServer(e.target.value)}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
                >
                  <option value="">— Select server —</option>
                  {servers.map((s) => (
                    <option key={s.id} value={s.inventoryName || s.id}>{s.name}</option>
                  ))}
                </select>
              )}
              {noServersAvailable && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  No servers available for the selected environment.
                </p>
              )}
            </div>

            {/* Version input with suggestions */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="deploy-version" className="block text-xs font-medium text-content-secondary">Version *</label>
                {latestVersion && (
                  <button
                    type="button"
                    onClick={() => selectVersion(latestVersion)}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-brand-700 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    Latest ({latestVersion})
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  ref={inputRef}
                  id="deploy-version"
                  type="text"
                  required
                  value={version}
                  onChange={(e) => {
                    setVersion(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="e.g. 1.2.3"
                  autoComplete="off"
                  aria-autocomplete="list"
                  aria-controls="version-suggestions"
                  aria-expanded={showSuggestions && filteredSuggestions.length > 0}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
                />
                {/* Suggestions dropdown */}
                {showSuggestions && !versionsLoading && filteredSuggestions.length > 0 && (
                  <ul
                    ref={suggestionsRef}
                    id="version-suggestions"
                    role="listbox"
                    className="absolute z-20 mt-1 w-full rounded-md border border-border bg-surface shadow-lg max-h-40 overflow-y-auto"
                  >
                    {filteredSuggestions.map((v: PackageVersion, idx: number) => (
                      <li
                        key={v.version}
                        role="option"
                        aria-selected={version === v.version}
                        onClick={() => selectVersion(v.version)}
                        className="flex items-center justify-between px-3 py-1.5 text-sm text-content hover:bg-surface-raised cursor-pointer"
                      >
                        <span className="font-mono">{v.version}</span>
                        <span className="flex items-center gap-1.5">
                          {idx === 0 && (
                            <span className="rounded-full bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:text-green-300">
                              latest
                            </span>
                          )}
                          <span className="text-xs text-content-tertiary">{v.status}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {showSuggestions && versionsLoading && (
                  <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-surface shadow-lg px-3 py-2">
                    <p className="text-xs text-content-tertiary">Loading versions...</p>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={deploying || !version.trim() || !selectedServer || noServersAvailable}
                className="flex-1 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
              >
                {deploying ? 'Deploying...' : 'Deploy'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={deploying}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// --- Main Component ---

interface DeployAppTabProps {
  projectKey: string;
  environmentId?: string;
}

interface DeploymentArtifactWithArtifact extends DeploymentArtifact {
  artifactName: string;
}

export function DeployAppTab({ projectKey, environmentId: _environmentId }: DeployAppTabProps) {
  const { data: artifacts = [], isLoading: artifactsLoading } = useArtifacts(projectKey);
  const { data: environments = [], isLoading: envsLoading } = useEnvironments(projectKey);
  const { data: serviceDefinitions = [] } = useServiceDefinitions(projectKey);
  const { invalidateArtifacts } = useInvalidateConfiguration(projectKey);

  const [deploymentArtifacts, setDeploymentArtifacts] = useState<DeploymentArtifactWithArtifact[]>([]);
  const [loadingDeployments, setLoadingDeployments] = useState(false);

  const loading = artifactsLoading || envsLoading || loadingDeployments;

  const [deployTarget, setDeployTarget] = useState<Artifact | null>(null);
  const [noEnvMessage, setNoEnvMessage] = useState(false);

  // Override editing state (Req 5.1–5.9)
  const [editingOverrideArtifactId, setEditingOverrideArtifactId] = useState<string | null>(null);
  const [resolvedConfigs, setResolvedConfigs] = useState<Record<string, ResolvedDeploymentConfig>>({});
  const [loadingResolved, setLoadingResolved] = useState(false);

  // Environment-scoped artifact deploy statuses (Req 9.1, 9.2, 9.3, 9.4, 9.5, 9.6)
  const {
    data: artifactStatusMap = {},
    isLoading: statusesLoading,
    isError: statusesError,
    refetch: refetchStatuses,
  } = useArtifactDeployStatuses(projectKey, _environmentId);

  // Clear noEnvMessage when environment is selected; close modal on environment change
  useEffect(() => {
    if (_environmentId) {
      setNoEnvMessage(false);
    }
    // Close deploy modal when environment changes (Req 8.5)
    setDeployTarget(null);
    // Close override editor when environment changes
    setEditingOverrideArtifactId(null);
  }, [_environmentId]);

  // Fetch resolved deployment configs for deployable artifacts (Req 5.1)
  const fetchResolvedConfigs = useCallback(async () => {
    if (!_environmentId || artifacts.length === 0) {
      setResolvedConfigs({});
      return;
    }
    setLoadingResolved(true);
    try {
      const configs = await getAllResolvedDeployments(projectKey, _environmentId);
      setResolvedConfigs(configs);
    } catch {
      setResolvedConfigs({});
    }
    setLoadingResolved(false);
  }, [_environmentId, artifacts, projectKey]);

  useEffect(() => {
    fetchResolvedConfigs();
  }, [fetchResolvedConfigs]);

  // Fetch all deployment artifacts for the project
  const fetchAllDeploymentArtifacts = useCallback(async () => {
    if (artifacts.length === 0) {
      setDeploymentArtifacts([]);
      return;
    }
    setLoadingDeployments(true);
    try {
      const results = await listAllDeploymentArtifacts(projectKey);
      setDeploymentArtifacts(
        results.map((da) => ({
          ...da,
          artifactName: artifacts.find((a) => a.id === da.artifactId)?.name ?? da.artifactId,
        }))
      );
    } catch {
      setDeploymentArtifacts([]);
    } finally {
      setLoadingDeployments(false);
    }
  }, [artifacts, projectKey]);

  useEffect(() => {
    if (!artifactsLoading && artifacts.length >= 0) {
      fetchAllDeploymentArtifacts();
    }
  }, [artifacts, artifactsLoading, fetchAllDeploymentArtifacts]);

  const deployableArtifacts = useMemo(
    () => [...artifacts].filter((a) => a.deployable).sort((a, b) => a.name.localeCompare(b.name)),
    [artifacts]
  );

  const handleDeploy = useCallback(async (artifact: Artifact, environmentId: string, version: string, server?: string) => {
    // Find a matching deployment artifact for this artifact+env to get deploymentType
    const matchingDA = deploymentArtifacts.find(
      (da) => da.artifactId === artifact.id && da.environmentId === environmentId
    );
    await triggerArtifactDeploy({
      projectKey,
      environment: environmentId,
      artifactId: artifact.id,
      version,
      deploymentType: matchingDA?.deploymentType ?? artifact.defaultDeploymentType,
      server,
    });
  }, [projectKey, deploymentArtifacts]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-content">Deployments</h3>
      </div>

      {/* Loading / Error / List */}
      {loading && <LoadingState />}

      {!loading && loadingResolved && (
        <p className="text-xs text-content-tertiary animate-pulse">Loading resolved configuration...</p>
      )}

      {!loading && deployableArtifacts.length === 0 && (
        <EmptyState message="No deployable artifacts. Mark artifacts as deployable on the Artifacts page to see them here." />
      )}

      {!loading && deployableArtifacts.length > 0 && (
        <ul className="space-y-2">
          {deployableArtifacts.map((art) => (
              <li key={art.id} className="rounded-lg border border-border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-content">{art.name}</p>
                    <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-content-secondary">
                      {art.downloadType && (
                        <span className="inline-flex items-center rounded-full bg-brand-100 dark:bg-brand-900/30 px-2 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-300">
                          {art.downloadType}
                        </span>
                      )}
                      {/* Resolved field values with source badges */}
                      {_environmentId && resolvedConfigs[art.id] && (() => {
                        const rc = resolvedConfigs[art.id]!;
                        const allNull = !rc.deployPath.value && !rc.downloadPath.value && !rc.port.value && !rc.ownerGroup.value && !rc.ownerName.value && !rc.serviceDefinitionId.value;
                        const overrideExists = deploymentArtifacts.some(
                          (o) => o.artifactId === art.id && o.environmentId === _environmentId
                        );
                        const existingDA = deploymentArtifacts.find(
                          (o) => o.artifactId === art.id && o.environmentId === _environmentId
                        );
                        const allOverrideFieldsNull = overrideExists && existingDA &&
                          !existingDA.downloadPath && !existingDA.deployPath && existingDA.port == null && !existingDA.ownerGroup && !existingDA.ownerName && !existingDA.serviceDefinitionId;

                        if (allNull && !overrideExists) return null;
                        if (allOverrideFieldsNull) {
                          return (
                            <span className="inline-flex items-center gap-1 text-content-tertiary">
                              <span className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-medium">Using defaults</span>
                            </span>
                          );
                        }
                        return (
                          <>
                            <OverrideFieldDisplay label="Deploy Path" value={rc.deployPath.value} source={rc.deployPath.source} />
                            <OverrideFieldDisplay label="Download Path" value={rc.downloadPath.value} source={rc.downloadPath.source} />
                            <OverrideFieldDisplay label="Port" value={rc.port.value} source={rc.port.source} />
                            <OverrideFieldDisplay label="Owner Group" value={rc.ownerGroup.value} source={rc.ownerGroup.source} />
                            <OverrideFieldDisplay label="Owner Name" value={rc.ownerName.value} source={rc.ownerName.source} />
                            <OverrideFieldDisplay label="Service" value={rc.serviceDefinitionId.value} source={rc.serviceDefinitionId.source} serviceDefinitions={serviceDefinitions} />
                          </>
                        );
                      })()}
                      {/* Fallback display when no resolved config */}
                      {(!_environmentId || !resolvedConfigs[art.id]) && (
                        <>
                          {art.defaultDeployPath && <span>Path: {art.defaultDeployPath}</span>}
                          {art.defaultPort != null && <span>Port: {art.defaultPort}</span>}
                        </>
                      )}
                    </div>
                    <ArtifactDeployStatusBadge
                      data={artifactStatusMap[art.id]}
                      isLoading={statusesLoading}
                      isError={statusesError}
                      onRetry={() => refetchStatuses()}
                    />
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (!_environmentId) {
                          setNoEnvMessage(true);
                          return;
                        }
                        setNoEnvMessage(false);
                        setDeployTarget(art);
                      }}
                      disabled={environments.length === 0}
                      className="inline-flex items-center gap-1.5 rounded-md bg-accent-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-800 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-2 transition-colors"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      Deploy
                    </button>
                    {_environmentId && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingOverrideArtifactId(
                            editingOverrideArtifactId === art.id ? null : art.id
                          );
                        }}
                        className={`rounded px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1 ${
                          editingOverrideArtifactId === art.id
                            ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                            : 'text-content-secondary hover:bg-surface-raised'
                        }`}
                      >
                        Override
                      </button>
                    )}
                  </div>
                </div>
                {/* Inline Override Edit Form */}
                {editingOverrideArtifactId === art.id && _environmentId && (() => {
                  const existingOverride = deploymentArtifacts.find(
                    (o) => o.artifactId === art.id && o.environmentId === _environmentId
                  );
                  return (
                    <div className="mt-3">
                      <OverrideEditForm
                        artifact={art}
                        environmentId={_environmentId}
                        projectKey={projectKey}
                        existingOverride={existingOverride}
                        resolvedConfig={resolvedConfigs[art.id] ?? null}
                        serviceDefinitions={serviceDefinitions}
                        onSaved={async () => {
                          await invalidateArtifacts();
                          await fetchAllDeploymentArtifacts();
                          await fetchResolvedConfigs();
                          setEditingOverrideArtifactId(null);
                        }}
                        onDeleted={async () => {
                          await invalidateArtifacts();
                          await fetchAllDeploymentArtifacts();
                          await fetchResolvedConfigs();
                          setEditingOverrideArtifactId(null);
                        }}
                        onCancel={() => setEditingOverrideArtifactId(null)}
                      />
                    </div>
                  );
                })()}
              </li>
          ))}
        </ul>
      )}

      {environments.length === 0 && !loading && (
        <p className="text-xs text-content-tertiary italic">
          No environments configured. Create environments in Admin before deploying.
        </p>
      )}

      {/* No environment selected inline message */}
      {noEnvMessage && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            An environment must be selected before deploying. Use the environment selector above.
          </p>
        </div>
      )}

      {/* Deploy Modal */}
      {deployTarget && _environmentId && (
        <DeployModal
          artifact={deployTarget}
          projectKey={projectKey}
          environmentId={_environmentId}
          onClose={() => setDeployTarget(null)}
          onDeploy={(envId, version, server) => handleDeploy(deployTarget, envId, version, server)}
        />
      )}
    </div>
  );
}
